import React, { Fragment, useEffect, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import Loader from "../../shared/Loader";
import { errorToast, successToast } from "../../shared/Toast";
import { fetchCompanyDetails, editCompanyDetails } from "../../utils/apiCalls";
import Breadcrumb from "../common/breadcrumb";
import { useNavigate, useParams } from "react-router-dom";

const SOCIAL_PLATFORMS = [
    { value: "instagram", label: "Instagram" },
    { value: "facebook", label: "Facebook" },
    { value: "twitter", label: "Twitter / X" },
    { value: "tiktok", label: "TikTok" },
    { value: "snapchat", label: "Snapchat" },
    { value: "youtube", label: "YouTube" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "pinterest", label: "Pinterest" },
    { value: "telegram", label: "Telegram" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "threads", label: "Threads" },
];

const isValidUrl = (string) => {
    try {
        const url = new URL(string);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

const EditCompanyConfiguration = () => {
    const { companyID } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [companyData, setCompanyData] = useState({
        merchantId: "",
        whatsappSupport: "",
        emailSupport: "",
        vatNumber: "",
        taxName: "",
        taxPercentage: "",
        name: "",
        arabicName: "",
        address: "",
        companyRegistery: "",
        phone: "",
        website: "",
        email: "",
        socialLinks: {},
    });
    const [socialLinkEntries, setSocialLinkEntries] = useState([]);

    useEffect(() => {
        const fetchCompanyAndSetData = async () => {
            setLoading(true);
            try {
                const response = await fetchCompanyDetails();
                if (!response.isError) {
                    const data = response.data;
                    const company = data?.find((item) => item?.id == companyID)
                    const socialLinks = company.socialLinks || {};
                    setCompanyData({
                        merchantId: company.merchantId || "",
                        whatsappSupport: company.whatsappSupport || "",
                        emailSupport: company.emailSupport || "",
                        vatNumber: company.vatNumber || "",
                        taxName: company.taxName || "",
                        taxPercentage: company.taxPercentage || "",
                        name: company.name || "",
                        arabicName: company.arabicName || "",
                        address: company.address || "",
                        companyRegistery: company.companyRegistery || "",
                        phone: company.phone || "",
                        website: company.website || "",
                        email: company.email || "",
                        socialLinks,
                    });
                    setSocialLinkEntries(
                        Object.keys(socialLinks).length > 0
                            ? Object.entries(socialLinks).map(([platform, url]) => ({ platform, url }))
                            : [{ platform: "", url: "" }]
                    );
                } else {
                    errorToast("Failed to fetch company details");
                }
            } catch (error) {
                console.error("Error fetching company details:", error);
                errorToast("Failed to fetch company details");
            } finally {
                setLoading(false);
            }
        };

        fetchCompanyAndSetData();
    }, []);

    const handleInputChange = (e, key) => {
        setCompanyData((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handleDiscard = () => {
        setCompanyData({
            merchantId: "",
            whatsappSupport: "",
            emailSupport: "",
            vatNumber: "",
            taxName: "",
            taxPercentage: "",
            name: "",
            arabicName: "",
            address: "",
            companyRegistery: "",
            phone: "",
            website: "",
            email: "",
            socialLinks: {},
        });
        setSocialLinkEntries([{ platform: "", url: "" }]);
    };

    const buildSocialLinks = (entries) => {
        const socialLinks = {};
        entries.forEach(({ platform, url }) => {
            if (platform.trim() && url.trim()) {
                socialLinks[platform.trim().toLowerCase()] = url.trim();
            }
        });
        return socialLinks;
    };

    const handleSocialLinkChange = (index, field, value) => {
        const updated = socialLinkEntries.map((entry, i) =>
            i === index ? { ...entry, [field]: value } : entry
        );
        setSocialLinkEntries(updated);
        setCompanyData((prev) => ({ ...prev, socialLinks: buildSocialLinks(updated) }));
    };

    const addSocialLink = () => {
        setSocialLinkEntries([...socialLinkEntries, { platform: "", url: "" }]);
    };

    const removeSocialLink = (index) => {
        const updated = socialLinkEntries.filter((_, i) => i !== index);
        const result = updated.length > 0 ? updated : [{ platform: "", url: "" }];
        setSocialLinkEntries(result);
        setCompanyData((prev) => ({ ...prev, socialLinks: buildSocialLinks(result) }));
    };

    const handleValidSubmit = async (e) => {
        e.preventDefault();

        for (const entry of socialLinkEntries) {
            if (entry.platform && !entry.url.trim()) {
                const label = SOCIAL_PLATFORMS.find((p) => p.value === entry.platform)?.label || entry.platform;
                errorToast(`Please enter a URL for ${label}`);
                return;
            }
            if (entry.platform && entry.url.trim() && !isValidUrl(entry.url.trim())) {
                const label = SOCIAL_PLATFORMS.find((p) => p.value === entry.platform)?.label || entry.platform;
                errorToast(`Invalid URL for ${label}. Please enter a valid URL (e.g. https://...)`);
                return;
            }
        }

        try {
            setLoading(true);

            const response = await editCompanyDetails(companyData);
            if (!response.isError) {
                successToast(response.message);
                navigate("/settings/companiesList");
            } else {
                errorToast(response.message);
            }
        } catch (error) {
            console.error("Error updating company details:", error);
            errorToast("Failed to update company details");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="Edit Company Configuration" parent="Company Configuration" />
            <Container fluid={true}>
                {/* {loading ? (
                    <Loader />
                ) : ( */}
                <Row>
                    <Col >
                        <Card>
                            <CardHeader>
                                <h5>Edit Company Configuration</h5>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col xl="4">
                                    </Col>
                                    <Col xl="8">

                                        <Form className="needs-validation add-product-form" onSubmit={handleValidSubmit}>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Name :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.name}
                                                        onChange={(e) => handleInputChange(e, "name")}
                                                        required
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Arabic Name :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.arabicName}
                                                        onChange={(e) => handleInputChange(e, "arabicName")}
                                                        required
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Address :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.address}
                                                        onChange={(e) => handleInputChange(e, "address")}
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Phone :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.phone}
                                                        onChange={(e) => handleInputChange(e, "phone")}
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Website :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.website}
                                                        onChange={(e) => handleInputChange(e, "website")}
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Company Registry :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.companyRegistery}
                                                        onChange={(e) => handleInputChange(e, "companyRegistery")}
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">VAT Number :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.vatNumber}
                                                        onChange={(e) => handleInputChange(e, "vatNumber")}
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Tax Name :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.taxName}
                                                        onChange={(e) => handleInputChange(e, "taxName")}
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Tax Percentage :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.taxPercentage}
                                                        onChange={(e) => handleInputChange(e, "taxPercentage")}
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Email :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="email"
                                                        value={companyData.email}
                                                        onChange={(e) => handleInputChange(e, "email")}
                                                        required
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">WhatsApp Support :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="text"
                                                        value={companyData.whatsappSupport}
                                                        onChange={(e) => handleInputChange(e, "whatsappSupport")}
                                                        required
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Email Support :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    <Input
                                                        className="form-control"
                                                        type="email"
                                                        value={companyData.emailSupport}
                                                        onChange={(e) => handleInputChange(e, "emailSupport")}
                                                        required
                                                    />
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="form-group mb-3 row">
                                                <Label className="col-xl-3 col-sm-4 mb-0">Social Links :</Label>
                                                <div className="col-xl-8 col-sm-7">
                                                    {socialLinkEntries.map((entry, index) => {
                                                        const selectedPlatforms = socialLinkEntries
                                                            .filter((_, i) => i !== index)
                                                            .map((e) => e.platform);
                                                        const availableOptions = SOCIAL_PLATFORMS.filter(
                                                            (p) => !selectedPlatforms.includes(p.value)
                                                        );
                                                        return (
                                                            <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                                                                <Input
                                                                    type="select"
                                                                    value={entry.platform}
                                                                    onChange={(e) => handleSocialLinkChange(index, "platform", e.target.value)}
                                                                    style={{ flex: 1 }}
                                                                >
                                                                    <option value="">Select Platform</option>
                                                                    {availableOptions.map((p) => (
                                                                        <option key={p.value} value={p.value}>
                                                                            {p.label}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                                <Input
                                                                    className="form-control"
                                                                    type="url"
                                                                    placeholder="https://..."
                                                                    value={entry.url}
                                                                    onChange={(e) => handleSocialLinkChange(index, "url", e.target.value)}
                                                                    style={{ flex: 2 }}
                                                                    invalid={entry.url !== "" && !isValidUrl(entry.url)}
                                                                />
                                                                <Button
                                                                    color="danger"
                                                                    size="sm"
                                                                    onClick={() => removeSocialLink(index)}
                                                                    title="Remove"
                                                                >
                                                                    ✕
                                                                </Button>
                                                            </div>
                                                        );
                                                    })}
                                                    {socialLinkEntries.length < SOCIAL_PLATFORMS.length && (
                                                        <Button
                                                            color="outline-primary"
                                                            size="sm"
                                                            className="mt-1 d-flex align-items-center gap-1"
                                                            onClick={addSocialLink}
                                                        >
                                                            <i className="fa fa-plus"></i> Add Social Link
                                                        </Button>
                                                    )}
                                                </div>
                                            </FormGroup>
                                            <div className="form-group mb-3 row">
                                                <div className="col-xl-8 offset-xl-3 col-sm-7 offset-sm-4 d-flex gap-2">
                                                    <Button type="submit" color="primary">
                                                        Save
                                                    </Button>
                                                    <Button color="secondary" className="ml-2" onClick={handleDiscard}>
                                                        Discard
                                                    </Button>
                                                </div>
                                            </div>
                                        </Form>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                {/* )} */}
            </Container>
        </Fragment>
    );
};

export default EditCompanyConfiguration;
